<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useMutation, useQuery } from '@/composables/api'

import useAuthStore from '@/stores/authStore'
import { useDialogsStore } from '@/stores/dialogsStore'

import type { CustomerUser } from '@/types/customer-users'

import BDialog from '@/components/base/BDialog/BDialog.vue'
import FContactFields from '@/components/feature/FContactFields/FContactFields.vue'
import FPayoutMethodFields from '@/components/feature/FPayoutMethodFields/FPayoutMethodFields.vue'
import { buildPayoutDetailsPatch, payoutGateBlocked } from '@/components/feature/FPayoutMethodFields/payoutDetails'
import type { PayoutMethod } from '@/components/feature/FPayoutMethodFields/types'

// Singleton profile dialog (ADA-4437 etapp 3) — replaces @old profile_dialog. Two pages:
// contact (mobile + email) and payout details; the request_invoice_copy context is a
// persistent mobile-only flow. Opens via dialogsStore.openProfile() — which ALWAYS opens:
// only the Home nudge gates on dontShowProfileDialog (deliberate fix of the legacy hang
// where the flag suppressed the invoice-copy flow too).

const { t } = useI18n()
const dialogsStore = useDialogsStore()

const model = computed({
  get: () => dialogsStore.profile != null,
  set: (value) => {
    // ESC / click-outside / header X — the skip path (the save path closes programmatically).
    if (!value) skipClose()
  },
})
const isMobileNumberOnly = computed(() => dialogsStore.profile?.context === 'request_invoice_copy')

const userQuery = useQuery('/customer--users')
const user = computed<CustomerUser | undefined>(() => userQuery.data.value?._items?.[0])

// The dialog's pages, in order. The header counter derives from this list — no literal page
// count in the template (PR #896 review).
const PAGES = ['contact', 'payout'] as const
type ProfilePage = (typeof PAGES)[number]
const page = ref<ProfilePage>('contact')
const pageNumber = computed(() => PAGES.indexOf(page.value) + 1)
const formValid = ref(false)
const email = ref('')
const mobileNumber = ref('')
// ADA-4345 step 2.15: the debtor picks how they want to be paid, not only a bank account.
// `payout_details` can physically hold every instrument at once, but the profile expresses a
// single choice — see buildUpdates.
const payoutMethod = ref<PayoutMethod>('bank_account')
const clearingNumber = ref('')
const accountNumber = ref('')
const bankgiro = ref('')
const plusgiro = ref('')

function storedPayoutMethod(): PayoutMethod {
  if (accountNumber.value !== '') return 'bank_account'
  if (bankgiro.value !== '') return 'bankgiro'
  if (plusgiro.value !== '') return 'plusgiro'
  return 'bank_account'
}

// Seed the fields when the dialog opens (and when the user data arrives while open).
// Legacy parity: the full flow jumps straight to the payout page when contact data already exists.
watch(
  () => [dialogsStore.profile != null, user.value?._id] as const,
  ([open]) => {
    if (!open || user.value == null) return
    email.value = user.value.email_data?.email ?? ''
    mobileNumber.value = user.value.mobile_number_data?.mobile_number ?? ''
    clearingNumber.value = user.value.payout_details?.bank_account_number?.clearing_number ?? ''
    accountNumber.value = user.value.payout_details?.bank_account_number?.account_number ?? ''
    bankgiro.value = user.value.payout_details?.bankgiro ?? ''
    plusgiro.value = user.value.payout_details?.plusgiro ?? ''
    // Same priority as the refund wizard's prefill (Overpayments.vue), so the profile opens on
    // the method the wizard would preselect. FPayoutMethodFields corrects it for a company.
    payoutMethod.value = storedPayoutMethod()
    page.value = !isMobileNumberOnly.value && email.value !== '' && mobileNumber.value !== '' ? 'payout' : 'contact'
  },
  { immediate: true }
)

// The giro numbers the record already carries — the reference for every change-aware check below.
const storedBankgiro = computed(() => user.value?.payout_details?.bankgiro ?? '')
const storedPlusgiro = computed(() => user.value?.payout_details?.plusgiro ?? '')

const authStore = useAuthStore()
// Bank transfer is hidden for a company (FPayoutMethodFields) — see buildPayoutDetailsPatch.
const isCompany = computed(() => authStore.selectedToken?.payload?.org_nbr != null)
const payoutFields = computed(() => ({
  method: payoutMethod.value,
  clearingNumber: clearingNumber.value,
  accountNumber: accountNumber.value,
  bankgiro: bankgiro.value,
  plusgiro: plusgiro.value,
}))

const primaryDisabled = computed(() => {
  // saveAndClose can only PATCH once the user document (with _id/_etag) has resolved —
  // submitting before that would silently drop the input and close the dialog.
  if (user.value == null) return true
  if (page.value === 'contact') {
    if (isMobileNumberOnly.value) return !formValid.value
    return email.value === '' || mobileNumber.value === '' || !formValid.value
  }
  // The three payout gates live in payoutDetails.ts, shared with the profile page.
  if (payoutGateBlocked(payoutFields.value, user.value.payout_details ?? {})) return true
  return !formValid.value
})

const patchUser = useMutation('patch', '/customer--users/{resource_id}', {
  // The dialog closing IS the confirmation (legacy parity) — errors still get the global snackbar.
  meta: { notifyOnSuccess: false },
})

function handlePrimary() {
  if (page.value === 'contact' && !isMobileNumberOnly.value) {
    page.value = 'payout'
  } else {
    void saveAndClose()
  }
}

// Diff-based PATCH (legacy parity): only changed fields are sent, contact changes reset
// `verified`, payout_details is patched as the whole merged object.
function buildUpdates(current: CustomerUser): Record<string, unknown> {
  const updates: Record<string, unknown> = {}
  const strippedMobile = mobileNumber.value.replaceAll(' ', '')
  if (strippedMobile !== (current.mobile_number_data?.mobile_number ?? '')) {
    updates.mobile_number_data = { mobile_number: strippedMobile, verified: false }
  }
  if (email.value !== (current.email_data?.email ?? '')) {
    updates.email_data = { email: email.value, verified: false }
  }
  const payoutDetails = buildPayoutDetailsPatch(current, payoutFields.value, { isCompany: isCompany.value })
  if (payoutDetails != null) {
    updates.payout_details = payoutDetails
  }
  // The address is deliberately NOT collected here (developer decision 19 Aug): this dialog is
  // what greets the debtor at login, and asking for a postal address in that prompt is more than
  // the moment warrants. It stays editable on the profile page, which is where
  // `overpayments.issue_address_missing` already sends anyone blocked by a missing one.

  // The mobile-only flow never shows the email or payout fields — drop their no-op diffs.
  if (isMobileNumberOnly.value) {
    delete updates.email_data
    delete updates.payout_details
  }
  return updates
}

async function saveAndClose() {
  const current = user.value
  if (current != null) {
    const updates = buildUpdates(current)
    if (Object.keys(updates).length > 0) {
      try {
        await patchUser.mutateAsync({
          path: { resource_id: current._id },
          header: { 'If-Match': current._etag },
          // @ts-expect-error Swagger lies (body only)
          body: updates,
        })
      } catch {
        return // error snackbar comes from the global handler; keep the dialog open
      }
    }
  }
  finishClose()
}

function skipClose() {
  finishClose()
}

// Every close path suppresses the Home nudge (legacy parity) — the flag no longer
// affects openProfile() flows, only the nudge gate.
function finishClose() {
  dialogsStore.dontShowProfileDialog = true
  sessionStorage.setItem('dont_show_profile_dialog', 'true')
  dialogsStore.closeProfile()
}

function afterLeave() {
  page.value = 'contact'
}
</script>

<template>
  <b-dialog
    v-model="model"
    :persistent="isMobileNumberOnly"
    hide-footer
    @after-leave="afterLeave"
  >
    <template #title>
      {{ t(page === 'contact' ? 'common.how_may_we_be_in_touch' : 'common.payout_details') }}
    </template>

    <template #header-actions>
      <span
        v-if="!isMobileNumberOnly"
        class="text-body-small text-no-wrap mr-2"
      >
        {{ pageNumber }} / {{ PAGES.length }}
      </span>
    </template>

    <v-form
      v-model="formValid"
      @submit.prevent="handlePrimary"
    >
      <div class="px-6">
        <f-contact-fields
          v-if="page === 'contact'"
          v-model:email="email"
          v-model:mobile-number="mobileNumber"
          :mobile-number-only="isMobileNumberOnly"
        />

        <f-payout-method-fields
          v-if="page === 'payout'"
          v-model:method="payoutMethod"
          v-model:clearing-number="clearingNumber"
          v-model:account-number="accountNumber"
          v-model:bankgiro="bankgiro"
          v-model:plusgiro="plusgiro"
          :stored-bankgiro="storedBankgiro"
          :stored-plusgiro="storedPlusgiro"
        />
      </div>

      <div class="d-flex pt-2 px-6 pb-4">
        <v-spacer />

        <v-btn
          variant="text"
          class="mr-2"
          @click="skipClose"
        >
          {{ t(isMobileNumberOnly ? 'common.cancel' : 'common.skip') }}
        </v-btn>
        <v-btn
          :disabled="primaryDisabled"
          :loading="patchUser.isPending.value"
          color="primary"
          variant="flat"
          type="submit"
        >
          {{ t(page === 'contact' && !isMobileNumberOnly ? 'common.next' : 'common.send_in') }}
        </v-btn>
      </div>
    </v-form>
  </b-dialog>
</template>
